import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, Target, RefreshCw, MessageSquare, BarChart3, Utensils, ArrowRight, Brain, Dumbbell, Menu, X } from 'lucide-react';
import PhoneMockVideo from '../components/PhoneMockVideo';
import LanguageSelector from '../components/LanguageSelector';
import translations from '../lib/translations';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }
};

function Navbar({ t }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isEn = t.lang === 'en';
  const privacyLink = isEn ? '/en/privacy' : '/personvern';

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const close = () => setMobileOpen(false);
      window.addEventListener('scroll', close);
      return () => window.removeEventListener('scroll', close);
    }
  }, [mobileOpen]);

  return (
    <nav data-testid="landing-topnav" className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter] duration-300 ${
      scrolled || mobileOpen ? 'bg-[#050505]/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
    }`}>
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7 h-14 sm:h-16 flex items-center justify-between">
        <Link to={isEn ? '/en' : '/'} className="font-['Barlow_Condensed'] font-bold text-xl sm:text-2xl tracking-wider text-white">
          TEMPO
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="#hva-er-tempo" className="text-sm text-white/60 hover:text-white transition-colors duration-180">{t.nav.what}</a>
          <a href="#hvordan" className="text-sm text-white/60 hover:text-white transition-colors duration-180">{t.nav.how}</a>
          <a href="#funksjoner" className="text-sm text-white/60 hover:text-white transition-colors duration-180">{t.nav.features}</a>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageSelector />
          <a
            href="#last-ned"
            data-testid="landing-download-cta-button"
            className="h-9 sm:h-10 px-4 sm:px-5 rounded-[12px] sm:rounded-[14px] bg-[#CCFF00] text-black font-semibold text-xs sm:text-sm flex items-center gap-2 hover:bg-[#D7FF3A] transition-[background-color,box-shadow] duration-180 shadow-[0_8px_24px_rgba(204,255,0,0.12)]"
          >
            {t.nav.download}
          </a>
          <button 
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 -mr-2 text-white/60 hover:text-white"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#050505]/95 backdrop-blur-xl">
          <div className="px-5 py-4 space-y-1">
            {[
              { href: '#hva-er-tempo', label: t.nav.what },
              { href: '#hvordan', label: t.nav.how },
              { href: '#funksjoner', label: t.nav.features },
              { href: '#last-ned', label: t.nav.download },
            ].map(link => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="block py-3 text-sm text-white/60 hover:text-white border-b border-white/5 last:border-0 transition-colors duration-180"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}

function Hero({ t }) {
  return (
    <section className="relative min-h-[100dvh] flex items-center pt-16 sm:pt-20 pb-8 sm:pb-16 overflow-hidden">
      <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(60% 60% at 30% 20%, rgba(204,255,0,0.06) 0%, rgba(204,255,0,0.00) 60%), radial-gradient(50% 60% at 80% 10%, rgba(90,200,250,0.04) 0%, rgba(90,200,250,0.00) 55%)' }} />
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7 w-full">
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">
          <motion.div {...fadeUp} className="order-1">
            <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#A0A0A0] mb-4 sm:mb-6">
              {t.hero.kicker}
            </p>
            <h1 className="font-['Barlow_Condensed'] font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-white mb-4 sm:mb-6">
              {t.hero.title1}<br />
              <span className="text-[#CCFF00]">{t.hero.title2}</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg text-white/55 max-w-md mb-6 sm:mb-10 leading-relaxed">
              {t.hero.body}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a href="#last-ned" className="h-[52px] sm:h-[58px] px-6 sm:px-8 rounded-[14px] sm:rounded-[16px] bg-[#CCFF00] text-black font-semibold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-[#D7FF3A] hover:shadow-[0_14px_48px_rgba(204,255,0,0.16)] active:scale-[0.99] transition-[background-color,box-shadow,transform] duration-180 shadow-[0_12px_40px_rgba(204,255,0,0.12)]">
                {t.hero.cta} <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#hva-er-tempo" className="h-[52px] sm:h-[58px] px-6 sm:px-8 rounded-[14px] sm:rounded-[16px] bg-transparent text-white border border-white/10 font-semibold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-white/5 active:bg-white/3 transition-[background-color,border-color] duration-180">
                {t.hero.secondary}
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="flex justify-center lg:justify-end order-2"
          >
            <PhoneMockVideo />
          </motion.div>
        </div>
      </div>
      <motion.div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2" animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}>
        <ChevronDown className="w-5 h-5 text-white/30" />
      </motion.div>
    </section>
  );
}

function WhatIsTempo({ t }) {
  const w = t.what;
  return (
    <section id="hva-er-tempo" className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#CCFF00]/60 mb-3 sm:mb-4">{w.kicker}</p>
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-4 sm:mb-6 leading-tight">
            {w.title1}<br />{w.title2}
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/50 leading-relaxed mb-6 sm:mb-8">{w.body}</p>
          <div className="w-full h-[1px] bg-white/5 mb-6 sm:mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              { title: w.clarity, body: w.clarityBody },
              { title: w.consistency, body: w.consistencyBody },
              { title: w.progress, body: w.progressBody },
            ].map((item, i) => (
              <div key={i}>
                <p className="font-['Barlow_Condensed'] font-bold text-3xl sm:text-4xl text-[#CCFF00] mb-2">{item.title}</p>
                <p className="text-sm text-white/40 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Pillars({ t }) {
  const icons = [Brain, Target, RefreshCw];
  return (
    <section className="py-14 sm:py-20 lg:py-28" data-testid="landing-pillars">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-16">
          <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#A0A0A0] mb-3 sm:mb-4">{t.pillars.kicker}</p>
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white">{t.pillars.title}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {t.pillars.items.map((p, i) => {
            const Icon = icons[i];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                className="group rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5 sm:p-6 md:p-8 hover:bg-white/[0.04] hover:border-white/10 transition-[background-color,border-color] duration-180">
                <div className="w-10 h-10 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center mb-4 sm:mb-5"><Icon className="w-5 h-5 text-[#CCFF00]" /></div>
                <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#CCFF00]/70 mb-2 sm:mb-3">{p.label}</p>
                <h3 className="font-['Barlow_Condensed'] font-bold text-lg sm:text-xl md:text-2xl text-white mb-2 sm:mb-3">{p.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HowItWorks({ t }) {
  return (
    <section id="hvordan" className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-16">
          <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#A0A0A0] mb-3 sm:mb-4">{t.howItWorks.kicker}</p>
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white">{t.howItWorks.title}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {t.howItWorks.steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay: i * 0.12, ease: [0.2, 0.8, 0.2, 1] }} className="relative">
              <div className="flex items-baseline gap-3 mb-3 sm:mb-4">
                <span className="font-['Barlow_Condensed'] font-bold text-5xl sm:text-6xl text-[#CCFF00]/10">{s.num}</span>
              </div>
              <h3 className="font-['Barlow_Condensed'] font-bold text-lg sm:text-xl md:text-2xl text-white mb-2 sm:mb-3">{s.title}</h3>
              <p className="text-sm text-white/45 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DailyExample({ t }) {
  const dailyIcons = [Dumbbell, Utensils, MessageSquare];
  return (
    <section className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-16">
          <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#A0A0A0] mb-3 sm:mb-4">{t.daily.kicker}</p>
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white">{t.daily.title}</h2>
        </motion.div>
        <div className="max-w-lg mx-auto space-y-3 sm:space-y-4">
          {t.daily.items.map((item, i) => {
            const Icon = dailyIcons[i];
            return (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.15, ease: [0.2, 0.8, 0.2, 1] }}
                className="rounded-[14px] sm:rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-4 sm:p-5 flex items-start gap-3 sm:gap-4">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-[#CCFF00]/8 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#CCFF00]/70" /></div>
                <div>
                  <p className="uppercase tracking-[0.18em] text-[9px] sm:text-[10px] font-medium text-white/30 mb-1 sm:mb-1.5">{item.time}</p>
                  <p className="text-xs sm:text-sm text-white/65 leading-relaxed italic">{item.msg}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
        <motion.p {...fadeUp} className="text-center text-xs sm:text-sm text-white/25 mt-6 sm:mt-8">{t.daily.tagline}</motion.p>
      </div>
    </section>
  );
}

function Features({ t }) {
  const featureIcons = [MessageSquare, BarChart3, Utensils];
  return (
    <section id="funksjoner" className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-16">
          <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#A0A0A0] mb-3 sm:mb-4">{t.features.kicker}</p>
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white">{t.features.title}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {t.features.items.map((f, i) => {
            const Icon = featureIcons[i];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }} transition={{ duration: 0.5, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                className="rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5 sm:p-6 md:p-8 hover:bg-white/[0.04] hover:border-white/10 transition-[background-color,border-color] duration-180">
                <div className="w-10 h-10 rounded-xl bg-[#5AC8FA]/10 flex items-center justify-center mb-4 sm:mb-5"><Icon className="w-5 h-5 text-[#5AC8FA]" /></div>
                <h3 className="font-['Barlow_Condensed'] font-bold text-lg sm:text-xl md:text-2xl text-white mb-2 sm:mb-3">{f.title}</h3>
                <p className="text-sm text-white/45 leading-relaxed">{f.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function NotForEveryone({ t }) {
  const n = t.notFor;
  return (
    <section className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="max-w-2xl mx-auto text-center">
          <p className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-medium text-[#A0A0A0] mb-3 sm:mb-4">{n.kicker}</p>
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-6 sm:mb-8">{n.title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 text-left">
            <div className="rounded-[14px] sm:rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-white/5 p-5 sm:p-6">
              <p className="uppercase tracking-[0.18em] text-[9px] sm:text-[10px] font-medium text-[#FF3B30]/60 mb-3">{n.notLabel}</p>
              <ul className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-white/40 leading-relaxed">
                {n.notItems.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
            <div className="rounded-[14px] sm:rounded-[16px] bg-[rgba(255,255,255,0.03)] border border-[#CCFF00]/10 p-5 sm:p-6">
              <p className="uppercase tracking-[0.18em] text-[9px] sm:text-[10px] font-medium text-[#CCFF00]/60 mb-3">{n.yesLabel}</p>
              <ul className="space-y-2 sm:space-y-2.5 text-xs sm:text-sm text-white/55 leading-relaxed">
                {n.yesItems.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function DownloadCTA({ t }) {
  return (
    <section id="last-ned" className="py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <motion.div {...fadeUp} className="rounded-[16px] sm:rounded-[20px] bg-[#121212] border border-white/5 p-6 sm:p-10 md:p-16 text-center">
          <h2 className="font-['Barlow_Condensed'] font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-2 sm:mb-3">{t.download.title}</h2>
          <p className="text-sm sm:text-base text-white/40 mb-6 sm:mb-10">{t.download.subtitle}</p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <a href="#" data-testid="landing-appstore-button" className="h-[52px] sm:h-[58px] px-6 sm:px-8 rounded-[14px] sm:rounded-[16px] bg-[#CCFF00] text-black font-semibold text-sm sm:text-base flex items-center justify-center gap-3 hover:bg-[#D7FF3A] hover:shadow-[0_14px_48px_rgba(204,255,0,0.16)] active:scale-[0.99] transition-[background-color,box-shadow,transform] duration-180 shadow-[0_12px_40px_rgba(204,255,0,0.12)]">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
              {t.download.appstore}
            </a>
            <a href="#" data-testid="landing-playstore-button" className="h-[52px] sm:h-[58px] px-6 sm:px-8 rounded-[14px] sm:rounded-[16px] bg-transparent text-white border border-white/10 font-semibold text-sm sm:text-base flex items-center justify-center gap-3 hover:bg-white/5 transition-[background-color,border-color] duration-180">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
              {t.download.playstore}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer({ t }) {
  const isEn = t.lang === 'en';
  const privacyLink = isEn ? '/en/privacy' : '/personvern';

  return (
    <footer className="border-t border-white/5 py-8 sm:py-12">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-7">
        <div className="flex flex-col gap-6 sm:gap-8">
          {/* Top row */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div>
              <p className="font-['Barlow_Condensed'] font-bold text-xl tracking-wider text-white mb-1 sm:mb-2">TEMPO</p>
              <p className="text-[10px] sm:text-xs text-white/25">&copy; {new Date().getFullYear()} Tempo. {t.footer.copyright}</p>
            </div>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              <Link to={privacyLink} className="text-[10px] sm:text-xs text-white/30 hover:text-white/60 transition-colors duration-180">{t.footer.privacy}</Link>
              <a href="#" className="text-[10px] sm:text-xs text-white/30 hover:text-white/60 transition-colors duration-180">{t.footer.terms}</a>
              <a href="#" className="text-[10px] sm:text-xs text-white/30 hover:text-white/60 transition-colors duration-180">{t.footer.contact}</a>
              <a href="/admin/login" className="text-[10px] sm:text-xs text-white/15 hover:text-white/40 transition-colors duration-180">Admin</a>
            </div>
          </div>
          {/* Company info */}
          <div className="pt-4 sm:pt-5 border-t border-white/[0.04]">
            <p className="text-[10px] sm:text-xs text-white/20">{t.footer.company} &middot; {t.footer.orgNr}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const location = useLocation();
  const lang = location.pathname.startsWith('/en') ? 'en' : 'no';
  const t = translations[lang];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar t={t} />
      <Hero t={t} />
      <WhatIsTempo t={t} />
      <Pillars t={t} />
      <HowItWorks t={t} />
      <DailyExample t={t} />
      <Features t={t} />
      <NotForEveryone t={t} />
      <DownloadCTA t={t} />
      <Footer t={t} />
    </div>
  );
}
